import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { attachLiveHls, type LiveHlsStatus } from '../lib/live-hls';
import { useNavigate } from 'react-router-dom';
import { AuthLayout } from '../components/layout';
import { Alert, SpinnerBlock } from '../components/core';

/** Public HLS player. Works with `?hls=<m3u8-url>` (embeds) or `/player/:id`
 * when the app can resolve the stream (authenticated viewer).
 */
export function PlayerPage() {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const { id: streamId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [hlsUrl, setHlsUrl] = useState<string | null>(null);
  const [status, setStatus] = useState<LiveHlsStatus>('connecting');
  const [detail, setDetail] = useState<string | null>(null);

// Try to get HLS URL from query param first (embed mode, no auth needed)
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const queryHlsUrl = searchParams.get('hls');

    if (queryHlsUrl) {
      setHlsUrl(queryHlsUrl);
    } else {
      setHlsUrl(null);
      if (streamId) {
        fetch(`/api/public/streams/${streamId}/hls-token`, {
          headers: { 'Content-Type': 'application/json' },
          credentials: 'omit',
        })
          .then((res) => (res.ok ? res.json() : null))
          .then((data) => {
            if (data && data.token) {
              // Construct HLS URL with signed token for protection
              // HLS base URL is configured at build time via Vite env
              setHlsUrl(`https://rtmphub.in/hls/${stream.ingestPath || 'live'}/index.m3u8?token=${data.token}`);
            }
          })
          .catch(() => {
            // ignore — player shows "provide stream" state
          });
      }
    }
  }, [streamId]);

  useEffect(() => {
    setStatus('connecting');
    setDetail(null);
    if (!hlsUrl || !videoRef.current) return;
    const handle = attachLiveHls(videoRef.current, hlsUrl, (next, info) => {
      setStatus(next);
      setDetail(info ?? null);
    });
    return () => handle.destroy();
  }, [hlsUrl]);

  if (!hlsUrl) {
    return (
      <AuthLayout title="Player" subtitle="Provide a stream to watch.">
        <Alert kind="info">
          This player needs an HLS URL. Open it from your Stream details page.\nOr use embed mode: ?hls={'<m3u8-url>'}
        </Alert>
        <div className="mt-2">
          <button className="btn secondary" onClick={() => navigate('/streams')}>Go to streams</button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--background)' }}>
      <div style={{ padding: '1rem' }}>
        <video ref={videoRef} controls autoPlay playsInline style={{ width: '100%', aspectRatio: '16/9', background: '#000' }} />
        {status === 'reconnecting' && (
          <div className="text-muted text-sm mt-2">Reconnecting… {detail ?? ''}</div>
        )}
        {status === 'error' && (
          <div className="text-danger text-sm mt-2">Stream unavailable — {detail ?? 'playback error'}</div>
        )}
        {status === 'playing' && (
          <div className="text-muted text-sm mt-2">
            Live playback · {hlsUrl.slice(0, 80)}…
          </div>
        )}
      </div>
    </div>
  );
}
